import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip
} from '@mui/material';

/**
 * Un componente SelectBox personalizzato che utilizza Material-UI.
 *
 * @param {object} props - Le props del componente.
 * @param {Array<object>} props.options - La lista di opzioni da visualizzare. Ogni opzione è un oggetto {key: string, value: string}.
 * @param {string} props.label - L'etichetta da mostrare sopra la select box.
 * @param {boolean} [props.multiple=false] - Se true, permette la selezione multipla.
 * @param {function} props.onSelectionChange - Callback che viene eseguita al cambio di selezione. Restituisce un array di oggetti {key, value} selezionati.
 * @param {Array<object>} [props.initialSelected=[]] - Opzionale. Array di oggetti {key, value} per pre-selezionare dei valori.
 */
const SelectBox = ({ options, label, multiple = false, onSelectionChange, initialSelected = [] }) => {
  // Stato per tenere traccia delle chiavi degli elementi selezionati
  const [selectedKeys, setSelectedKeys] = useState(() => {
    const initialKeys = initialSelected.map(item => item.key);
    return multiple ? initialKeys : (initialKeys[0] || '');
  });

  // Sincronizza lo stato se le props iniziali cambiano
  useEffect(() => {
    const initialKeys = initialSelected.map(item => item.key);
    setSelectedKeys(multiple ? initialKeys : (initialKeys[0] || ''));
  }, [initialSelected, multiple]);


  const handleChange = (event) => {
    const { target: { value } } = event;

    // Aggiorna lo stato interno con le chiavi selezionate
    setSelectedKeys(value);

    // Prepara l'output per la callback onSelectionChange
    let selectedItems = [];
    if (multiple) {
      // Filtra le opzioni originali per trovare gli oggetti completi corrispondenti alle chiavi
      selectedItems = options.filter(option => value.includes(option.key));
    } else {
      // Trova l'oggetto singolo corrispondente alla chiave
      const selectedOption = options.find(option => option.key === value);
      if (selectedOption) {
        selectedItems = [selectedOption];
      }
    }

    // Esegui la callback passando l'array di oggetti {key, value}
    onSelectionChange(selectedItems);
  };

  // Funzione per renderizzare i valori selezionati nella modalità multipla (mostra dei "Chip")
  const renderMultipleValue = (selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((key) => {
        const option = options.find(opt => opt.key === key);
        return <Chip key={key} label={option ? option.value : key} />;
      })}
    </Box>
  );


  return (
    <FormControl fullWidth>
      <InputLabel id={`select-label-${label}`}>{label}</InputLabel>
      <Select
        labelId={`select-label-${label}`}
        id={`select-${label}`}
        multiple={multiple}
        value={selectedKeys}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        // Usa la funzione custom per renderizzare i valori solo se multipla è true
        renderValue={multiple ? renderMultipleValue : undefined}
      >
        {/* Se non è multipla, aggiungi un'opzione vuota per deselezionare */}
        {!multiple && <MenuItem value=""><em>Nessuna</em></MenuItem>}

        {/* Mappa le opzioni passate come props per creare i MenuItem */}
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Definizione dei tipi delle props per una migliore manutenibilità e debug
SelectBox.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  label: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  onSelectionChange: PropTypes.func.isRequired,
  initialSelected: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
};

export default SelectBox;